import { useState, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Loader2, Lock, Users, Clock, LayoutDashboard, Trophy,
  SlidersHorizontal, CalendarDays, Dumbbell, ClipboardList, UserRound,
} from "lucide-react";
import { API, HOLDING, splitList } from "./admin/shared";
import WaitlistTab from "./admin/WaitlistTab";
import RegistrationsTab from "./admin/RegistrationsTab";
import LeaguesTab from "./admin/LeaguesTab";
import MembershipTab from "./admin/MembershipTab";
import MembersTab from "./admin/MembersTab";
import SessionsTab from "./admin/SessionsTab";
import MessagingTab from "./admin/MessagingTab";
import ProgramsTab from "./admin/ProgramsTab";
import EnrollmentsTab from "./admin/EnrollmentsTab";
import CustomersTab from "./admin/CustomersTab";
import ProgramWaitlistTab from "./admin/ProgramWaitlistTab";
import AgreementViewer from "./admin/AgreementViewer";
import LegalReviewTab from "./admin/LegalReviewTab";
import { ScrollText, Wallet } from "lucide-react";
import CreditsTab, { IssueCreditModal } from "./admin/CreditsTab";
import { toEditorProgram, fromEditorProgram } from "./admin/ProgramFormFields";
import { addDaysISO } from "@/lib/dates";
import AdminModals from "./admin/AdminModals";
import OverviewTab from "./admin/OverviewTab";
import TrainingTab from "./admin/TrainingTab";
import AttendanceTab from "./admin/AttendanceTab";
import FamiliesTab from "./admin/FamiliesTab";
import CommsTab from "./admin/CommsTab";
import PaymentsTab from "./admin/PaymentsTab";
import CategoriesTab from "./admin/CategoriesTab";
import LessonsTab from "./admin/LessonsTab";
import PlayersTab from "./admin/PlayersTab";
import FinanceTab from "./admin/ops/FinanceTab";
import WaiversTab from "./admin/ops/WaiversTab";
import ScoringTab from "./admin/scoring/ScoringTab";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { CalendarCheck, Users2, Mail, CreditCard, Settings2 } from "lucide-react";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "lessons", label: "Calendar & Lessons", icon: CalendarCheck },
  { key: "enrollments", label: "Registrations", icon: ClipboardList },
  { key: "finance", label: "Finances", icon: Wallet },
  { key: "waivers", label: "Agreements & Waivers", icon: ScrollText },
  { key: "attendance", label: "Rosters & Attendance", icon: CalendarCheck },
  { key: "players", label: "Athletes", icon: Users2 },
  { key: "scoring", label: "Scoring & Leaderboards", icon: Trophy },
  { key: "training", label: "Classes & Prices", icon: Dumbbell },
  { key: "families", label: "Families", icon: Users2 },
  { key: "comms", label: "Communications", icon: Mail },
  { key: "payments", label: "Payments", icon: CreditCard },
  { key: "program-waitlist", label: "Waitlist", icon: ClipboardList },
  { key: "credits", label: "Credits", icon: Wallet },
  { key: "categories", label: "Settings", icon: Settings2 },
  { key: "legal", label: "Legal Review", icon: ScrollText },
];

export default function Admin() {
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [view, setView] = useState("overview");
  const [role, setRole] = useState("admin");
  const enterCoach = (t) => { sessionStorage.setItem("replab_staff_token", t); setToken(t); setRole("coach"); setView("scoring"); setError(""); setAuthed(true); };
  const [commsPreset, setCommsPreset] = useState(null);
  const [data, setData] = useState(null);
  const [waitlist, setWaitlist] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [viewing, setViewing] = useState(null);
  const [settings, setSettings] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [editingDiv, setEditingDiv] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersSummary, setMembersSummary] = useState({});
  const [memberStatusFilter, setMemberStatusFilter] = useState("");
  const [addingMember, setAddingMember] = useState(null);
  const [promoting, setPromoting] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgForm, setMsgForm] = useState({ segment: "all_members", division_id: "", subject: "", body: "" });
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [crmMember, setCrmMember] = useState(null);
  const [crmDetail, setCrmDetail] = useState(null);
  const [crmLoading, setCrmLoading] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [sessions, setSessions] = useState([]);
  const [editingSession, setEditingSession] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [editingProgram, setEditingProgram] = useState(null);
  const [programRegs, setProgramRegs] = useState(null);
  const [programRegsLoading, setProgramRegsLoading] = useState(false);
  const [disciplines, setDisciplines] = useState([]);
  const [editingDiscipline, setEditingDiscipline] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [editingEnrollment, setEditingEnrollment] = useState(null);
  const [movingEnrollment, setMovingEnrollment] = useState(null);
  const [manualEnroll, setManualEnroll] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [enrollFilters, setEnrollFilters] = useState({ q: "", program_id: "", program_type: "", payment_status: "", enrollment_status: "", media: "", waiver: "" });
  const [programWaitlist, setProgramWaitlist] = useState([]);

  const headers = useCallback((t) => ({ "X-Admin-Token": t || token }), [token]);

  const updateReg = async (id, patch) => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/registrations/${id}`, patch, { headers: headers() });
      const res = await axios.get(`${API}/admin/registrations`, { headers: headers() });
      setData(res.data);
      setViewing((v) => (v && v.id === id ? { ...v, ...patch } : v));
    } finally {
      setLoading(false);
    }
  };

  const loadAll = useCallback(async (t) => {
    setLoading(true);
    setError("");
    try {
      const [regRes, lgRes, wlRes, setRes, dvRes, mbRes, msgRes, snRes, pgRes, dcRes, enRes, cuRes, pwRes] = await Promise.all([
        axios.get(`${API}/admin/registrations`, { headers: { "X-Admin-Token": t } }),
        axios.get(`${API}/admin/leagues`, { headers: { "X-Admin-Token": t } }),
        axios.get(`${API}/admin/waitlist`, { headers: { "X-Admin-Token": t } }),
        axios.get(`${API}/admin/settings`, { headers: { "X-Admin-Token": t } }),
        axios.get(`${API}/admin/divisions`, { headers: { "X-Admin-Token": t } }),
        axios.get(`${API}/admin/members`, { headers: { "X-Admin-Token": t } }),
        axios.get(`${API}/admin/messages`, { headers: { "X-Admin-Token": t } }),
        axios.get(`${API}/admin/sessions`, { headers: { "X-Admin-Token": t } }),
        axios.get(`${API}/admin/programs`, { headers: { "X-Admin-Token": t } }),
        axios.get(`${API}/admin/discipline`, { headers: { "X-Admin-Token": t } }),
        axios.get(`${API}/admin/enrollments`, { headers: { "X-Admin-Token": t } }),
        axios.get(`${API}/admin/customers`, { headers: { "X-Admin-Token": t } }),
        axios.get(`${API}/admin/program-waitlist`, { headers: { "X-Admin-Token": t } }),
      ]);
      setData(regRes.data);
      setLeagues(lgRes.data);
      setWaitlist(wlRes.data);
      setSettings(setRes.data);
      setDivisions(dvRes.data);
      setMembers(mbRes.data.members);
      setMembersSummary(mbRes.data.summary);
      setMessages(msgRes.data.messages);
      setSessions(snRes.data);
      setPrograms(pgRes.data.programs || []);
      setDisciplines(dcRes.data.disciplines || []);
      setEnrollments(enRes.data.enrollments || []);
      setCustomers(cuRes.data.customers || []);
      setProgramWaitlist(pwRes.data.waitlist || []);
      setAuthed(true);
    } catch (e) {
      if (e?.response?.status === 401) setError("Invalid access code.");
      else setError("Something went wrong. Try again.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshLeagues = async () => setLeagues((await axios.get(`${API}/admin/leagues`, { headers: headers() })).data);
  const refreshDivisions = async () => setDivisions((await axios.get(`${API}/admin/divisions`, { headers: headers() })).data);
  const refreshMembers = async () => {
    const res = await axios.get(`${API}/admin/members`, { headers: headers() });
    setMembers(res.data.members);
    setMembersSummary(res.data.summary);
  };
  const refreshSessions = async () => setSessions((await axios.get(`${API}/admin/sessions`, { headers: headers() })).data);
  const refreshWaitlist = async () => setWaitlist((await axios.get(`${API}/admin/waitlist`, { headers: headers() })).data);
  const refreshPrograms = async () => setPrograms((await axios.get(`${API}/admin/programs`, { headers: headers() })).data.programs || []);
  const refreshEnrollments = async () => setEnrollments((await axios.get(`${API}/admin/enrollments`, { headers: headers() })).data.enrollments || []);
  const refreshCustomers = async () => setCustomers((await axios.get(`${API}/admin/customers`, { headers: headers() })).data.customers || []);
  const refreshProgramWaitlist = async () => setProgramWaitlist((await axios.get(`${API}/admin/program-waitlist`, { headers: headers() })).data.waitlist || []);

  const setWaitlistStatus = async (id, status) => {
    try {
      await axios.post(`${API}/admin/program-waitlist/${id}/status`, { status }, { headers: headers() });
      await refreshProgramWaitlist();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not update status.");
    }
  };

  const resendConfirmation = async (registrationId, force = false) => {
    try {
      const { data } = await axios.post(`${API}/admin/enrollments/${registrationId}/resend-confirmation`, { force }, { headers: headers() });
      window.alert(`Confirmation sent to ${data.sent_to}.`);
      await refreshEnrollments();
    } catch (e) {
      if (e?.response?.status === 409 && !force) {
        if (window.confirm(`${e.response.data.detail}\n\nSend a copy now?`)) await resendConfirmation(registrationId, true);
        return;
      }
      window.alert(e?.response?.data?.detail || "Could not send confirmation email.");
    }
  };

  const [agreementView, setAgreementView] = useState(null);
  const [creditFor, setCreditFor] = useState(null);
  const [creditsKey, setCreditsKey] = useState(0);
  const openProgramAgreement = async (p) => {
    try {
      const { data } = await axios.get(`${API}/admin/programs/${p.id}/agreement-preview`, { headers: headers() });
      setAgreementView({ mode: "preview", title: p.title, agreements: [data], missing: data.missing, advisories: data.advisories });
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Could not generate agreement preview.");
    }
  };
  const openEnrollmentAgreement = async (registrationId) => {
    try {
      const { data } = await axios.get(`${API}/admin/enrollments/${registrationId}/agreement-record`, { headers: headers() });
      const lg = data.legal_acceptance || {};
      const m = lg.master_agreement || {};
      const media = (lg.media_authorization || {}).choice;
      setAgreementView({
        mode: "record", registrationId, moves: data.program_moves || [],
        title: `${(lg.participant || {}).name || "Participant"} — ${data.programs.map((p) => p.title).join(", ")}`,
        agreements: lg.enrollment_agreements || [],
        summary: [
          ["Guardian", (lg.guardian || {}).name], ["Guardian email", (lg.guardian || {}).email],
          ["Participant", (lg.participant || {}).name], ["Date of birth", (lg.participant || {}).dob],
          ["Typed signature", (lg.electronic_signature || {}).typed_name],
          ["Accepted at", lg.accepted_at && new Date(lg.accepted_at).toLocaleString()],
          ["Master Agreement", m.version && `${m.title} v${m.version}`],
          ["Media authorization (Sec. 12)", media === "agree" ? "YES" : media === "decline" ? "NO" : "—"],
          ["Payment status", data.payment.status], ["Stripe session", data.payment.stripe_checkout_session_id],
        ],
      });
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Could not load agreement record.");
    }
  };
  const resolveReviewFlag = async (registrationId, flagId) => {
    try {
      await axios.post(`${API}/admin/enrollments/${registrationId}/review-flags/${flagId}/resolve`, {}, { headers: headers() });
      await refreshEnrollments();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not update review flag.");
    }
  };
  const downloadAgreementRecord = async (registrationId, format) => {
    try {
      const res = await axios.get(`${API}/admin/enrollments/${registrationId}/agreement-record`, {
        headers: headers(), params: { format }, responseType: format === "html" ? "text" : "json",
      });
      const body = format === "html" ? res.data : JSON.stringify(res.data, null, 2);
      const blob = new Blob([body], { type: format === "html" ? "text/html" : "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `replab-agreement-${registrationId}.${format}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Could not download record.");
    }
  };

  // Opens an unsaved copy for the next cycle; nothing is created until the admin saves it.
  const duplicateProgram = (id) => {
    const p = programs.find((x) => x.id === id);
    if (!p) return;
    const { id: _omit, registered, remaining, display_status, agreement_missing, agreement_advisories, ...reusable } = p; // eslint-disable-line no-unused-vars
    setError("");
    setEditingProgram(toEditorProgram({
      ...reusable,
      title: (() => {
        const s = addDaysISO(p.start_date, 28);
        const base = (p.title || "").replace(/\s*\((Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]* \d{4}\)$/, "");
        return s ? `${base} (${new Date(`${s}T12:00:00`).toLocaleString("en-US", { month: "short" })} ${s.slice(0, 4)})` : base;
      })(),
      start_date: addDaysISO(p.start_date, 28),
      end_date: addDaysISO(p.end_date, 28),
      status: "draft",
      next_program_id: "",
      duplicate_of: p.title,
    }));
  };

  const setProgramStatus = async (id, status) => {
    const prog = programs.find((x) => x.id === id);
    if (status === "open" && prog?.agreement_missing?.length) {
      window.alert(`This program can't be opened yet. Its Program Enrollment Agreement is missing: ${prog.agreement_missing.join(", ")}. Edit the program to add these details.`);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/programs/${id}/status`, { status }, { headers: headers() });
      await refreshPrograms();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not update status.");
    } finally {
      setLoading(false);
    }
  };

  const saveEnrollment = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/enrollments/${editingEnrollment.registration_id}`, {
        athlete_name: editingEnrollment.athlete_name,
        athlete_age: editingEnrollment.athlete_age,
        division: editingEnrollment.division,
        parent_name: editingEnrollment.parent_name,
        email: editingEnrollment.email,
        phone: editingEnrollment.phone,
        payment_status: editingEnrollment.payment_status,
        enrollment_status: editingEnrollment.enrollment_status,
        media_consent: editingEnrollment.media_consent || "",
        amount: editingEnrollment.amount,
      }, { headers: headers() });
      setEditingEnrollment(null);
      await Promise.all([refreshEnrollments(), refreshCustomers(), refreshPrograms()]);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save enrollment.");
    } finally {
      setLoading(false);
    }
  };

  const moveEnrollment = async (confirmDiff = false) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/enrollments/${movingEnrollment.registration_id}/move`, {
        from_program_id: movingEnrollment.program_id,
        to_program_id: movingEnrollment.to_program_id,
        override_capacity: !!movingEnrollment.override_capacity,
        confirm_price_difference: confirmDiff,
      }, { headers: headers() });
      setMovingEnrollment(null);
      if (data.price_difference) window.alert(`Moved. Price difference of $${Math.abs(data.price_difference).toFixed(2)} was recorded — collect it, or issue a credit, manually if appropriate.`);
      await Promise.all([refreshEnrollments(), refreshCustomers(), refreshPrograms()]);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.price_difference !== undefined) {
        setLoading(false);
        if (window.confirm(d.message)) await moveEnrollment(true);
        return;
      }
      setError(typeof d === "string" ? d : "Could not move athlete.");
    } finally {
      setLoading(false);
    }
  };

  const removeEnrollmentProgram = async (registrationId, programId) => {
    const reason = window.prompt("Cancel this registration? Enter a reason (recorded in the audit log). No refund or credit is issued automatically — use Issue Credit or record a refund separately.");
    if (reason === null) return;
    setLoading(true);
    try {
      await axios.post(`${API}/admin/enrollments/${registrationId}/remove-program`, { program_id: programId, reason }, { headers: headers() });
      await Promise.all([refreshEnrollments(), refreshCustomers(), refreshPrograms()]);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not remove enrollment.");
    } finally {
      setLoading(false);
    }
  };

  const saveManualEnrollment = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/enrollments/manual`, {
        program_id: manualEnroll.program_id,
        athlete: { name: manualEnroll.athlete_name, age_grade: manualEnroll.athlete_age, division: manualEnroll.division },
        parent: { name: manualEnroll.parent_name, email: manualEnroll.email, phone: manualEnroll.phone },
        emergency: { name: manualEnroll.emergency_name, phone: manualEnroll.emergency_phone },
        payment_status: manualEnroll.payment_status,
        media_consent: manualEnroll.media_consent || "",
        amount: manualEnroll.amount === "" || manualEnroll.amount == null ? null : Number(manualEnroll.amount),
        override_capacity: !!manualEnroll.override_capacity,
      }, { headers: headers() });
      setManualEnroll(null);
      await Promise.all([refreshEnrollments(), refreshCustomers(), refreshPrograms()]);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not create enrollment.");
    } finally {
      setLoading(false);
    }
  };

  const saveCustomer = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/customers`, {
        email: editingCustomer.email,
        parent_name: editingCustomer.parent_name,
        phone: editingCustomer.phone,
        new_email: editingCustomer.new_email || undefined,
      }, { headers: headers() });
      setEditingCustomer(null);
      await Promise.all([refreshCustomers(), refreshEnrollments()]);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save customer.");
    } finally {
      setLoading(false);
    }
  };

  const saveProgram = async () => {
    if (editingProgram.start_date && editingProgram.end_date && editingProgram.end_date < editingProgram.start_date) {
      setError("End date cannot be earlier than the start date.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...fromEditorProgram(editingProgram),
        age_group: (editingProgram.age_group || "").trim(),
        sessions_count: Number(editingProgram.sessions_count),
        capacity: Number(editingProgram.capacity),
        price: Number(editingProgram.price),
        sort_order: Number(editingProgram.sort_order),
        session_minutes: Number(editingProgram.session_minutes || 0),
      };
      if (editingProgram.id) {
        const { data } = await axios.put(`${API}/admin/programs/${editingProgram.id}`, payload, { headers: headers() });
        if (data.flagged_registrations > 0) {
          window.alert(`${data.flagged_registrations} existing registration(s) accepted the previous program details. Their signed agreements were NOT changed — they are flagged for review in Enrollments.`);
        }
      } else {
        await axios.post(`${API}/admin/programs`, payload, { headers: headers() });
      }
      setEditingProgram(null);
      await refreshPrograms();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save program.");
    } finally {
      setLoading(false);
    }
  };

  const deleteProgram = async (id) => {
    if (!window.confirm("Delete this program? This cannot be undone.")) return;
    setLoading(true);
    try {
      await axios.delete(`${API}/admin/programs/${id}`, { headers: headers() });
      await refreshPrograms();
    } finally {
      setLoading(false);
    }
  };

  const openProgramRegs = async (program) => {
    setProgramRegs({ program, registrations: [], waitlist: [] });
    setProgramRegsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/programs/${program.id}/registrations`, { headers: headers() });
      setProgramRegs({ program, registrations: res.data.registrations || [], waitlist: res.data.waitlist || [] });
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load registrations.");
    } finally {
      setProgramRegsLoading(false);
    }
  };

  const saveDiscipline = async () => {
    setLoading(true);
    try {
      const payload = {
        headline: editingDiscipline.headline,
        hero_primary: editingDiscipline.hero_primary,
        hero_secondary: editingDiscipline.hero_secondary,
        develop_headline: editingDiscipline.develop_headline,
        develop: String(editingDiscipline.develop || "").split("\n").map((s) => s.trim()).filter(Boolean),
        how_headline: editingDiscipline.how_headline,
        how_tools: editingDiscipline.how_tools,
        how_emphasis: editingDiscipline.how_emphasis,
        how_final: editingDiscipline.how_final,
      };
      const res = await axios.put(`${API}/admin/discipline/${editingDiscipline.key}`, payload, { headers: headers() });
      setDisciplines((ds) => ds.map((d) => (d.key === res.data.key ? res.data : d)));
      setEditingDiscipline(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save page content.");
    } finally {
      setLoading(false);
    }
  };

  const saveSession = async () => {
    setLoading(true);
    try {
      const payload = {
        division_id: editingSession.division_id,
        name: editingSession.name,
        day_of_week: editingSession.day_of_week,
        time: editingSession.time,
        capacity: Number(editingSession.capacity),
        sort_order: Number(editingSession.sort_order),
        status: editingSession.status || "active",
      };
      if (editingSession.id) {
        await axios.put(`${API}/admin/sessions/${editingSession.id}`, payload, { headers: headers() });
      } else {
        await axios.post(`${API}/admin/sessions`, payload, { headers: headers() });
      }
      setEditingSession(null);
      await refreshSessions();
      await refreshMembers();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save session.");
    } finally {
      setLoading(false);
    }
  };

  const archiveSession = async (id) => {
    if (!window.confirm("Archive this session? Assigned members will be unassigned.")) return;
    setLoading(true);
    try {
      await axios.delete(`${API}/admin/sessions/${id}`, { headers: headers() });
      await refreshSessions();
      await refreshMembers();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not archive session.");
    } finally {
      setLoading(false);
    }
  };

  const assignSession = async (memberId, sessionId, fromCrm) => {
    setLoading(true);
    setCrmLoading(true);
    try {
      await axios.post(`${API}/admin/members/${memberId}/assign-session`, { session_id: sessionId || null }, { headers: headers() });
      await refreshMembers();
      await refreshSessions();
      if (fromCrm && crmMember) {
        const res = await axios.get(`${API}/admin/members/${crmMember}/detail`, { headers: headers() });
        setCrmDetail(res.data);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not assign session.");
    } finally {
      setLoading(false);
      setCrmLoading(false);
    }
  };

  const waitlistAction = async (id, action, body) => {
    if (action === "delete" && !window.confirm("Remove this waitlist entry?")) return;
    setLoading(true);
    try {
      if (action === "delete") {
        await axios.delete(`${API}/admin/waitlist/${id}`, { headers: headers() });
      } else {
        await axios.post(`${API}/admin/waitlist/${id}/${action}`, body || {}, { headers: headers() });
      }
      await refreshWaitlist();
    } catch (e) {
      setError(e?.response?.data?.detail || "Action failed.");
    } finally {
      setLoading(false);
    }
  };

  const promoteWaitlist = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/waitlist/${promoting.id}/promote`, { division_id: promoting.division_id }, { headers: headers() });
      setPromoting(null);
      await refreshWaitlist();
      await refreshMembers();
      await refreshDivisions();
    } catch (e) {
      setError(e?.response?.data?.detail || "Promote failed.");
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    setSending(true);
    setSendResult(null);
    setError("");
    try {
      const { data: res } = await axios.post(`${API}/admin/messages/send`, msgForm, { headers: headers() });
      setSendResult(res);
      setMsgForm({ ...msgForm, subject: "", body: "" });
      const msgRes = await axios.get(`${API}/admin/messages`, { headers: headers() });
      setMessages(msgRes.data.messages);
    } catch (e) {
      setError(e?.response?.data?.detail || "Send failed.");
    } finally {
      setSending(false);
    }
  };

  const memberAction = async (id, action, body) => {
    if (action === "cancel" && !window.confirm("Cancel this membership at period end?")) return;
    if (action === "refund" && !window.confirm("Refund the member's last payment?")) return;
    setLoading(true);
    try {
      if (action === "delete") {
        await axios.delete(`${API}/admin/members/${id}`, { headers: headers() });
      } else if (action === "portal") {
        const res = await axios.post(`${API}/admin/members/${id}/portal`, {}, { headers: headers() });
        window.open(res.data.url, "_blank");
      } else {
        await axios.post(`${API}/admin/members/${id}/${action}`, body || {}, { headers: headers() });
      }
      await refreshMembers();
      await refreshDivisions();
    } catch (e) {
      setError(e?.response?.data?.detail || "Action failed.");
    } finally {
      setLoading(false);
    }
  };

  const addMember = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/members`, addingMember, { headers: headers() });
      setAddingMember(null);
      await refreshMembers();
      await refreshDivisions();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not add member.");
    } finally {
      setLoading(false);
    }
  };

  const openMemberProfile = async (id) => {
    setCrmMember(id);
    setCrmDetail(null);
    setNoteText("");
    setCrmLoading(true);
    try {
      const res = await axios.get(`${API}/admin/members/${id}/detail`, { headers: headers() });
      setCrmDetail(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load member profile.");
    } finally {
      setCrmLoading(false);
    }
  };

  const addNote = async () => {
    const text = noteText.trim();
    if (!text || !crmMember) return;
    setCrmLoading(true);
    try {
      await axios.post(`${API}/admin/members/${crmMember}/notes`, { text }, { headers: headers() });
      setNoteText("");
      const res = await axios.get(`${API}/admin/members/${crmMember}/detail`, { headers: headers() });
      setCrmDetail(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not add note.");
    } finally {
      setCrmLoading(false);
    }
  };

  const deleteNote = async (noteId) => {
    if (!crmMember) return;
    setCrmLoading(true);
    try {
      await axios.delete(`${API}/admin/members/${crmMember}/notes/${noteId}`, { headers: headers() });
      const res = await axios.get(`${API}/admin/members/${crmMember}/detail`, { headers: headers() });
      setCrmDetail(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not delete note.");
    } finally {
      setCrmLoading(false);
    }
  };

  const crmAction = async (action) => {
    if (!crmMember) return;
    if (action === "pause" && !window.confirm("Pause this membership? Billing stops and their roster spot is freed for others.")) return;
    setCrmLoading(true);
    try {
      await axios.post(`${API}/admin/members/${crmMember}/${action}`, {}, { headers: headers() });
      const res = await axios.get(`${API}/admin/members/${crmMember}/detail`, { headers: headers() });
      setCrmDetail(res.data);
      await refreshMembers();
      await refreshDivisions();
    } catch (e) {
      setError(e?.response?.data?.detail || "Action failed.");
    } finally {
      setCrmLoading(false);
    }
  };

  const updateMode = async (mode) => {
    setLoading(true);
    try {
      const res = await axios.put(`${API}/admin/settings`, { registration_mode: mode }, { headers: headers() });
      setSettings(res.data);
    } finally {
      setLoading(false);
    }
  };

  const saveDivision = async () => {
    const payload = {
      name: editingDiv.name,
      capacity: Number(editingDiv.capacity),
      sort_order: Number(editingDiv.sort_order),
      filled_adjustment: Number(editingDiv.filled_adjustment || 0),
      monthly_price: Number(editingDiv.monthly_price ?? 199),
      status: editingDiv.status || "active",
    };
    setLoading(true);
    try {
      if (editingDiv.id) {
        await axios.put(`${API}/admin/divisions/${editingDiv.id}`, payload, { headers: headers() });
      } else {
        await axios.post(`${API}/admin/divisions`, payload, { headers: headers() });
      }
      setEditingDiv(null);
      await refreshDivisions();
    } catch (e) {
      setError("Could not save division.");
    } finally {
      setLoading(false);
    }
  };

  const archiveDivision = async (id) => {
    if (!window.confirm("Archive this division? It will be hidden from the website.")) return;
    setLoading(true);
    try {
      await axios.delete(`${API}/admin/divisions/${id}`, { headers: headers() });
      await refreshDivisions();
    } finally {
      setLoading(false);
    }
  };

  const saveLeague = async () => {
    const payload = {
      ...editing,
      price: Number(editing.price),
      spots_total: Number(editing.spots_total),
      sort_order: Number(editing.sort_order),
      age_groups: splitList(editing.age_groups),
      divisions: splitList(editing.divisions),
      included: splitList(editing.included),
      tracked: splitList(editing.tracked),
    };
    setLoading(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/leagues/${editing.id}`, payload, { headers: headers() });
      } else {
        await axios.post(`${API}/admin/leagues`, payload, { headers: headers() });
      }
      setEditing(null);
      await refreshLeagues();
    } catch (e) {
      setError("Could not save league.");
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    const rows = data.registrations || [];
    const header = ["Date", "Player", "Parent", "Competitive Division", "Email", "Phone", "Program", "Amount", "Payment Status", "Waiver Signed", "Roster Confirmed", "Medical Notes"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = rows.map((r) => [
      r.created_at,
      `${r.player?.first_name || ""} ${r.player?.last_name || ""}`.trim(),
      `${r.parent?.first_name || ""} ${r.parent?.last_name || ""}`.trim(),
      r.division,
      r.parent?.email,
      r.parent?.phone,
      r.league_name,
      r.amount,
      r.payment_status,
      r.waiver?.signature_name ? "Yes" : "No",
      r.roster_confirmed ? "Yes" : "No",
      r.medical?.notes,
    ].map(esc).join(","));
    downloadCSV([header.join(","), ...lines].join("\n"), "replab-registrations");
  };

  const exportWaitlistCSV = () => {
    const rows = (waitlist && waitlist.waitlist) || [];
    const header = ["Lead ID", "Date Joined", "Player", "Parent", "Email", "Phone", "School Division", "Current Team", "Questions"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = rows.map((r) => [
      r.lead_id, r.created_at, r.player_name, r.parent_name, r.email, r.phone, r.school_division, r.current_team, r.questions,
    ].map(esc).join(","));
    downloadCSV([header.join(","), ...lines].join("\n"), "replab-waitlist");
  };

  const downloadCSV = (csv, prefix) => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${prefix}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // --- Derived / memoized lists ---
  const filteredWaitlist = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ((waitlist && waitlist.waitlist) || []).filter((r) => {
      if (!q) return true;
      return `${r.player_name} ${r.parent_name} ${r.email} ${r.school_division}`.toLowerCase().includes(q);
    });
  }, [waitlist, search]);

  const filteredRegistrations = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ((data && data.registrations) || []).filter((r) => {
      if (!q) return true;
      return `${r.player?.first_name} ${r.player?.last_name} ${r.parent?.first_name} ${r.parent?.last_name} ${r.parent?.email}`.toLowerCase().includes(q);
    });
  }, [data, search]);

  const filteredMembers = useMemo(
    () => members.filter((m) => !memberStatusFilter || m.status === memberStatusFilter),
    [members, memberStatusFilter]
  );

  const activeSessions = useMemo(() => sessions.filter((s) => s.status === "active"), [sessions]);

  const sessionsByDivision = useMemo(() => {
    const map = {};
    for (const s of sessions) {
      if (s.status !== "active") continue;
      (map[s.division_id] ||= []).push(s);
    }
    return map;
  }, [sessions]);

  const membersBySession = useMemo(() => {
    const map = {};
    for (const m of members) {
      if (m.session_id) (map[m.session_id] ||= []).push(m);
    }
    return map;
  }, [members]);

  const unassignedMembers = useMemo(
    () => members.filter((m) => HOLDING.includes(m.status) && !m.session_id),
    [members]
  );

  const recipientCount = useMemo(() => {
    const s = msgForm.segment;
    if (s === "all_members") return members.filter((m) => HOLDING.includes(m.status)).length;
    if (s === "division") return members.filter((m) => HOLDING.includes(m.status) && m.division_id === msgForm.division_id).length;
    if (s === "canceled") return members.filter((m) => m.status === "canceled").length;
    if (s === "waitlist") return ((waitlist && waitlist.waitlist) || []).filter((w) => ["waiting", "invited"].includes(w.status || "waiting")).length;
    return 0;
  }, [msgForm, members, waitlist]);

  const divSessionsFor = useCallback((divisionId) => sessionsByDivision[divisionId] || [], [sessionsByDivision]);
  const sessionMembersFor = useCallback((sessionId) => membersBySession[sessionId] || [], [membersBySession]);
  const location = useLocation();
  const navigate = useNavigate();
  const processed = useRef(false);
  useEffect(() => {
    const m = (location.hash || "").match(/session_id=([^&]+)/);
    if (!m || processed.current) return;
    processed.current = true;
    navigate("/admin", { replace: true });
    axios.post(`${API}/auth/session`, { session_id: m[1] }).then(({ data }) => {
      if (data.role !== "admin") { enterCoach(data.token); return; }
      sessionStorage.setItem("replab_staff_token", data.token);
      setToken(data.token);
      loadAll(data.token);
    }).catch((e) => setError(e?.response?.data?.detail || "Google sign-in failed."));
  }, [location.hash]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = sessionStorage.getItem("replab_staff_token");
    if (t && !authed && !(location.hash || "").includes("session_id=")) {
      axios.get(`${API}/auth/me`, { headers: { "X-Admin-Token": t } }).then(({ data }) => {
        if (data.role === "admin") { setToken(t); loadAll(t); } else { enterCoach(t); }
      }).catch(() => { sessionStorage.removeItem("replab_staff_token"); setError("Your staff session expired. Please sign in again."); });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleSignIn = () => { window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/admin")}`; };

  if (!authed) {
    return (
      <div className="min-h-[100svh] bg-[#0B0F19] flex items-center justify-center px-5">
        <div className="w-full max-w-sm rounded-3xl bg-white/5 border border-white/10 p-9">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#2563EB]/15 text-[#2563EB] mb-6">
            <Lock size={22} />
          </div>
          <h1 className="text-2xl font-extrabold tracking-tight text-white">Rep Lab Admin</h1>
          <p className="mt-2 text-sm font-medium text-white/50">Sign in with Google or enter your access code.</p>
          <form onSubmit={(e) => { e.preventDefault(); if (token) loadAll(token); }} className="mt-7">
            <input
              data-testid="admin-token-input"
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Access code"
              className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white placeholder-white/30 outline-none focus:border-[#2563EB] transition-colors"
            />
            {error && <p data-testid="admin-error" className="mt-3 text-sm font-semibold text-red-400">{error}</p>}
            <button
              data-testid="admin-login-btn"
              type="submit"
              disabled={loading || !token}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1d4fd7] disabled:opacity-50 text-white font-bold px-6 py-4 rounded-full transition-colors"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : "Sign In"}
            </button>
          </form>
          <div className="my-5 flex items-center gap-3 text-xs font-bold uppercase tracking-wider text-white/30"><span className="h-px flex-1 bg-white/10" />or<span className="h-px flex-1 bg-white/10" /></div>
          <button data-testid="admin-google-signin-btn" type="button" onClick={googleSignIn} className="w-full rounded-full border border-white/20 text-white font-bold px-6 py-4 hover:bg-white/10 transition-colors">Sign in with Google</button>
        </div>
      </div>
    );
  }

  const ctx = {
    token, loading, error, view, setView, commsPreset, setCommsPreset,
    data, waitlist, leagues, settings, divisions, members, membersSummary, messages, sessions,
    search, setSearch,
    editing, setEditing, viewing, setViewing, editingDiv, setEditingDiv,
    memberStatusFilter, setMemberStatusFilter, addingMember, setAddingMember,
    promoting, setPromoting, msgForm, setMsgForm, sending, sendResult,
    crmMember, setCrmMember, crmDetail, setCrmDetail, crmLoading, noteText, setNoteText,
    editingSession, setEditingSession,
    programs, editingProgram, setEditingProgram: (p) => { if (!p || !("time24" in p)) setError(""); setEditingProgram(p && !("time24" in p) ? toEditorProgram(p) : p); }, setError, programRegs, setProgramRegs, programRegsLoading,
    disciplines, editingDiscipline, setEditingDiscipline,
    enrollments, customers,
    programWaitlist, refreshProgramWaitlist, setWaitlistStatus, resendConfirmation,
    editingEnrollment, setEditingEnrollment, movingEnrollment, setMovingEnrollment,
    manualEnroll, setManualEnroll, editingCustomer, setEditingCustomer,
    enrollFilters, setEnrollFilters,
    // handlers
    loadAll, updateReg, saveSession, archiveSession, assignSession,
    waitlistAction, promoteWaitlist, sendMessage, memberAction, addMember,
    openMemberProfile, addNote, deleteNote, crmAction, updateMode,
    saveDivision, archiveDivision, saveLeague, deleteLeague: async (id) => {
      if (!window.confirm("Delete this league? This cannot be undone.")) return;
      setLoading(true);
      try {
        await axios.delete(`${API}/admin/leagues/${id}`, { headers: headers() });
        await refreshLeagues();
      } finally {
        setLoading(false);
      }
    },
    exportCSV, exportWaitlistCSV, refreshMembers, refreshSessions,
    refreshPrograms, saveProgram, deleteProgram, openProgramRegs, saveDiscipline,
    duplicateProgram, setProgramStatus, openProgramAgreement, openEnrollmentAgreement, downloadAgreementRecord, resolveReviewFlag, setCreditFor,
    refreshEnrollments, refreshCustomers, saveEnrollment, moveEnrollment,
    removeEnrollmentProgram, saveManualEnrollment, saveCustomer,
    // derived
    filteredWaitlist, filteredRegistrations, filteredMembers,
    activeSessions, unassignedMembers, recipientCount,
    divSessionsFor, sessionMembersFor,
  };

  return (
    <div className="min-h-[100svh] bg-[#0B0F19] py-12 px-5 md:px-12">
      <div className="max-w-6xl mx-auto">
        {/* Tabs */}
        <div className="flex items-center gap-2 mb-10 flex-wrap">
          {TABS.filter((t) => role === "admin" || t.key === "scoring").map((t) => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setView(t.key)}
              className={`inline-flex items-center gap-2 text-sm font-bold rounded-full px-5 py-2.5 transition-colors ${view === t.key ? "bg-[#2563EB] text-white" : "text-white/60 border border-white/15 hover:text-white"}`}
            >
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {error && !editingProgram && (
          <div data-testid="admin-error-banner" className="mb-6 flex items-start justify-between gap-4 rounded-2xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm font-semibold text-red-300">
            <span>{error}</span>
            <button data-testid="admin-error-dismiss" onClick={() => setError("")} className="text-red-200/70 hover:text-red-100">Dismiss</button>
          </div>
        )}
        {view === "scoring" && <ScoringTab ctx={ctx} />}
        {role === "coach" && (
          <button data-testid="coach-signout-btn" onClick={() => { sessionStorage.removeItem("replab_staff_token"); window.location.reload(); }} className="mt-10 text-xs font-bold text-white/40 hover:text-white">Sign out</button>
        )}
        {role === "admin" && view === "overview" && <OverviewTab ctx={ctx} />}
        {view === "training" && <TrainingTab ctx={ctx} />}
        {role === "admin" && view === "finance" && <FinanceTab ctx={ctx} />}
        {role === "admin" && view === "waivers" && <WaiversTab ctx={ctx} />}
        {view === "attendance" && <AttendanceTab ctx={ctx} />}
        {view === "families" && <FamiliesTab ctx={ctx} />}
        {view === "comms" && <CommsTab ctx={ctx} />}
        {view === "payments" && <PaymentsTab ctx={ctx} />}
        {view === "categories" && <CategoriesTab ctx={ctx} />}
        {view === "players" && <PlayersTab ctx={ctx} />}
        {view === "lessons" && <LessonsTab ctx={ctx} />}
        {view === "programs" && <ProgramsTab ctx={ctx} />}
        {view === "enrollments" && <EnrollmentsTab ctx={ctx} />}
        {view === "customers" && <CustomersTab ctx={ctx} />}
        {view === "program-waitlist" && <ProgramWaitlistTab ctx={ctx} />}
        {view === "legal" && <LegalReviewTab ctx={ctx} />}
        {view === "credits" && <CreditsTab key={creditsKey} ctx={ctx} />}
        {view === "waitlist" && <WaitlistTab ctx={ctx} />}
        {view === "registrations" && <RegistrationsTab ctx={ctx} />}
        {view === "leagues" && <LeaguesTab ctx={ctx} />}
        {view === "membership" && <MembershipTab ctx={ctx} />}
        {view === "members" && <MembersTab ctx={ctx} />}
        {view === "sessions" && <SessionsTab ctx={ctx} />}
        {view === "messaging" && <MessagingTab ctx={ctx} />}
      </div>

      <AdminModals ctx={ctx} />
      <IssueCreditModal enrollment={creditFor} token={token} onClose={() => setCreditFor(null)} onDone={() => { setCreditFor(null); setCreditsKey((k) => k + 1); window.alert("Credit issued. See the Credits tab."); }} />
      <AgreementViewer view={agreementView} onClose={() => setAgreementView(null)} onDownload={downloadAgreementRecord} />
    </div>
  );
}
